import { useEffect, useRef, useState } from "react";

const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

/** Whether a file fits an <input accept> list such as "application/pdf,image/*,.json". */
export const matchesAccept = (file, accept) => {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return accept.split(",").map((a) => a.trim().toLowerCase()).some((a) =>
    a.startsWith(".") ? name.endsWith(a) : a.endsWith("/*") ? type.startsWith(a.slice(0, -1)) : type === a);
};

/** Drag and drop files onto an element (spread `bind` on it), or anywhere on the page with `page`.
 *  `dragging` is true while files are held over the target. Files that don't match `accept` go to `onReject`. */
export const useFileDrop = ({ onFiles, onReject, accept, multiple = false, disabled = false, page = false }) => {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const opts = useRef({});
  opts.current = { onFiles, onReject, accept, multiple, disabled };

  const handlers = useRef({
    enter: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      if (!opts.current.disabled) setDragging(true);
    },
    over: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = opts.current.disabled ? "none" : "copy";
    },
    leave: (e) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    drop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      const { onFiles: take, onReject: reject, accept: types, multiple: many, disabled: off } = opts.current;
      if (off) return;
      const files = Array.from(e.dataTransfer.files || []);
      const ok = files.filter((f) => matchesAccept(f, types));
      const refused = files.filter((f) => !matchesAccept(f, types));
      if (ok.length) take(many ? ok : ok.slice(0, 1));
      if (refused.length) reject?.(refused);
    },
  }).current;

  useEffect(() => {
    if (disabled) setDragging(false);
  }, [disabled]);

  useEffect(() => {
    if (page) {
      window.addEventListener("dragenter", handlers.enter);
      window.addEventListener("dragover", handlers.over);
      window.addEventListener("dragleave", handlers.leave);
      window.addEventListener("drop", handlers.drop);
      return () => {
        window.removeEventListener("dragenter", handlers.enter);
        window.removeEventListener("dragover", handlers.over);
        window.removeEventListener("dragleave", handlers.leave);
        window.removeEventListener("drop", handlers.drop);
      };
    }
    const guard = (e) => {
      if (!hasFiles(e) || e.defaultPrevented) return;
      e.preventDefault();
      if (e.type === "dragover") e.dataTransfer.dropEffect = "none";
    };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => {
      window.removeEventListener("dragover", guard);
      window.removeEventListener("drop", guard);
    };
  }, [page, handlers]);

  const bind = page ? {} : { onDragEnter: handlers.enter, onDragOver: handlers.over, onDragLeave: handlers.leave, onDrop: handlers.drop };
  return { dragging, bind };
};
